/**
 * Funnel step 8: order summary + upsells, after elegir-peana. No backend
 * or Kie.ai call — reads the customer's own chosen image (same resolution
 * order as figurine-showcase.js / base-picker.js) and price
 * (funnel-pricing.js), then writes { upsellType, quantity, figures,
 * activeFigureIndex, differentPetDiscount } into the shared funnel state
 * (funnel-state.js). Real cart/checkout wiring is a later phase — the
 * "Ajouter au panier"/twin buttons only confirm the choice in state for now.
 */
(() => {
  const root = document.querySelector('[data-finalize-page]');
  if (!root) return;

  // Test mode (?test=1): renders with local, in-memory mock data when no
  // real figure exists yet, so the page can be reviewed without running
  // the funnel or spending Kie.ai credits. No sessionStorage write, no
  // network call — purely local. Ignored entirely when a real figure
  // already exists, and has zero effect without the query param.
  const isTestMode = new URLSearchParams(window.location.search).get('test') === '1';
  const MOCK_STATE = {
    petName: 'Milo',
    price: 79.99,
    quantity: 1,
    selectedStyle: 'realistic',
    selectedBase: 'bois-gazon',
    engravingName: 'Milo',
    size: 'M',
    upsellType: null,
    differentPetDiscount: 0.35,
  };

  const state = window.PfFunnel ? window.PfFunnel.get() : {};
  const contentEl = root.querySelector('[data-finalize-content]');
  const errorEl = root.querySelector('[data-finalize-error]');

  const result = state.generationStatus === 'completed' ? state.generationResult : null;

  const STYLE_FIELD_MAP = {
    realistic: 'realisticUrl',
    animated_3d: 'animated3dUrl',
  };
  const selectedStyleField = STYLE_FIELD_MAP[state.selectedStyle];
  const selectedStyleUrl = result && selectedStyleField && result[selectedStyleField];
  const plainImageUrl =
    (result && result.frontViewUrl) ||
    selectedStyleUrl ||
    (result && (result.realisticUrl || result.figureImageUrl));

  // "Socle bois & gazon" real composite (see base-picker.js) — only trusted
  // when it was generated for the CURRENTLY selected style, same guard as
  // base-picker.js itself. This page never triggers or polls for it; it
  // only displays whatever's already in state, exactly as-is or falls back
  // to the plain figure — checkout-figurine.js is where finalizing an
  // order actually waits on it (see its own comment for why).
  const baseCompositeValid = state.selectedBase === 'wood_grass' && state.baseCompositeStyle === state.selectedStyle;
  const realImageUrl = (baseCompositeValid && state.baseCompositeUrl) || plainImageUrl;

  const usingMock = !realImageUrl && isTestMode;
  const imageUrl = realImageUrl || (usingMock ? window.PfMockFigureUrl : null);
  const petName = usingMock ? MOCK_STATE.petName : state.petName || 'ta mascotte';

  if (!imageUrl) {
    if (contentEl) contentEl.hidden = true;
    if (errorEl) errorEl.hidden = false;
    return;
  }

  if (contentEl) contentEl.hidden = false;
  if (errorEl) errorEl.hidden = true;

  root.querySelectorAll('[data-finalize-heading], [data-finalize-twin-copy]').forEach((el) => {
    el.textContent = el.textContent.replace('[PET_NAME]', petName);
  });

  const image = root.querySelector('[data-finalize-image]');
  if (image) {
    image.src = imageUrl;
    image.hidden = false;
  }

  const pricing = window.PfPricing;
  const price = usingMock ? MOCK_STATE.price : pricing.getPrice(state);
  const twinDiscounted = price * (1 - pricing.TWIN_DISCOUNT);

  const priceEl = root.querySelector('[data-finalize-price]');
  if (priceEl) priceEl.textContent = pricing.formatPrice(price);

  const twinDiscountedEl = root.querySelector('[data-finalize-twin-discounted]');
  const twinOriginalEl = root.querySelector('[data-finalize-twin-original]');
  if (twinDiscountedEl) twinDiscountedEl.textContent = pricing.formatPrice(twinDiscounted);
  if (twinOriginalEl) twinOriginalEl.textContent = pricing.formatPrice(price);

  const twinPercentEl = root.querySelector('[data-finalize-twin-percent]');
  if (twinPercentEl) twinPercentEl.textContent = '-' + Math.round(pricing.TWIN_DISCOUNT * 100) + '%';

  const differentPercentEl = root.querySelector('[data-finalize-different-percent]');
  if (differentPercentEl) differentPercentEl.textContent = '-' + Math.round(pricing.DIFFERENT_PET_DISCOUNT * 100) + '%';

  const confirmEl = root.querySelector('[data-finalize-confirm]');
  function showConfirm(message) {
    if (!confirmEl) return;
    confirmEl.textContent = message;
    confirmEl.hidden = false;
  }

  // Next step (order summary + real checkout handoff, see
  // checkout-figurine.js). Propagates ?test=1 so a mock review continues
  // in mock mode on the next page instead of erroring.
  function goToCheckout() {
    window.location.href = usingMock ? '/pages/checkout-figurine?test=1' : '/pages/checkout-figurine';
  }

  root.querySelector('[data-finalize-add-main]')?.addEventListener('click', () => {
    if (!usingMock) window.PfFunnel.set({ upsellType: null, quantity: 1 });
    goToCheckout();
  });

  // Twin = exact copy of the already-configured figure (same mascot, style,
  // base, name). No new AI task, no extra Kie.ai call — just quantity 2
  // with the second unit discounted.
  root.querySelector('[data-finalize-add-twin]')?.addEventListener('click', () => {
    if (!usingMock) window.PfFunnel.set({ upsellType: 'twin', quantity: 2 });
    goToCheckout();
  });

  // Different pet: snapshot the current figure into state.figures, then
  // reset only the per-figure working fields (not email) so nombre-mascota
  // starts a genuinely new mascot instead of showing figure 1's data.
  root.querySelector('[data-finalize-different-pet]')?.addEventListener('click', () => {
    if (usingMock) {
      showConfirm('[Test] Autre animal (35% de réduction) — aucune donnée réelle modifiée, aucune navigation.');
      return;
    }
    const current = window.PfFunnel.get();
    const figure1 = {
      petName: current.petName,
      email: current.email,
      petNotes: current.petNotes,
      selectedStyle: current.selectedStyle,
      selectedBase: current.selectedBase,
      generationResult: current.generationResult,
      masterTaskId: current.masterTaskId,
      sideTaskId: current.sideTaskId,
      baseCompositeTaskId: current.baseCompositeTaskId,
      baseCompositeStatus: current.baseCompositeStatus,
      baseCompositeUrl: current.baseCompositeUrl,
      baseCompositeStyle: current.baseCompositeStyle,
      designId: current.designId || null,
      price,
      upsellType: current.upsellType || null,
      quantity: current.quantity || 1,
    };
    const figures = Array.isArray(current.figures) ? current.figures.concat([figure1]) : [figure1];

    window.PfFunnel.set({
      figures,
      activeFigureIndex: figures.length,
      differentPetDiscount: pricing.DIFFERENT_PET_DISCOUNT,
      petName: undefined,
      petNotes: undefined,
      petPhotoMeta: undefined,
      generationStatus: undefined,
      generationProgress: undefined,
      generationResult: undefined,
      generationJobId: undefined,
      generationError: undefined,
      masterTaskId: undefined,
      selectedStyle: undefined,
      selectedStyleConfirmed: undefined,
      selectedBase: undefined,
      sideStatus: undefined,
      sideTaskId: undefined,
      baseCompositeTaskId: undefined,
      baseCompositeStatus: undefined,
      baseCompositeUrl: undefined,
      baseCompositeStyle: undefined,
      upsellType: undefined,
      quantity: undefined,
    });

    const clearPhoto = window.PfFunnelPhotoDB ? window.PfFunnelPhotoDB.clearPhoto() : Promise.resolve();
    Promise.resolve(clearPhoto)
      .catch(() => {})
      .then(() => {
        window.location.href = '/pages/nombre-mascota';
      });
  });
})();
